import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  getStatements,
  financeExportUrl,
  statementsXlsxUrl,
  type MonthlyLine,
  type Statements,
} from "@/lib/api";
import { openStatementsPdf } from "@/pages/finance/statements-print";
import {
  StatementDrilldown,
  type DrillLine,
  type DrillState,
} from "@/pages/finance/StatementDrilldown";
import { fmtMoney } from "@/lib/money";

const MONTH_LABEL = (m: string) =>
  new Date(`${m}-01`).toLocaleString("en-GB", { month: "short" });

/** Signed money: red + minus for negatives, blank for zero. */
function Cell({ value, strong }: { value: number; strong?: boolean }) {
  if (value === 0) return <span className="text-text-muted">—</span>;
  return (
    <span
      className={strong ? "font-semibold" : ""}
      style={{ color: value < 0 ? "var(--color-danger)" : undefined }}
    >
      {value < 0 ? "−" : ""}
      {fmtMoney(Math.abs(value))}
    </span>
  );
}

export function StatementsTab() {
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(thisYear);
  const [drill, setDrill] = useState<DrillState | null>(null);
  const { data, isLoading } = useQuery({
    queryKey: ["fin-statements", year],
    queryFn: () => getStatements(year),
  });

  if (isLoading || !data) {
    return (
      <div className="grid place-items-center rounded-token-lg border border-border bg-bg-card py-20 text-text-muted">
        <i className="fa-solid fa-circle-notch fa-spin text-lg" />
      </div>
    );
  }

  return (
    <div>
      {/* Headline cards */}
      <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <HeadlineCard
          label="Cash at bank"
          value={fmtMoney(data.headline.cash)}
          icon="fa-building-columns"
        />
        <HeadlineCard
          label="Monthly burn (3-mo avg)"
          value={data.headline.burn > 0 ? fmtMoney(data.headline.burn) : "—"}
          icon="fa-fire"
          tone={data.headline.burn > 0 ? "danger" : undefined}
        />
        <HeadlineCard
          label="Runway"
          value={
            data.headline.runwayMonths == null
              ? "∞"
              : `${data.headline.runwayMonths} months`
          }
          icon="fa-road"
          tone={
            data.headline.runwayMonths != null && data.headline.runwayMonths < 6
              ? "danger"
              : undefined
          }
        />
        <HeadlineCard
          label="Recurring / month"
          value={data.recurringTotal > 0 ? fmtMoney(data.recurringTotal) : "—"}
          icon="fa-rotate"
        />
      </div>

      {/* Year picker + export */}
      <div className="mb-4 flex items-center gap-2">
        {[thisYear - 1, thisYear].map((y) => (
          <button
            key={y}
            onClick={() => setYear(y)}
            className={`rounded-token-md border px-3 py-1.5 text-sm transition-colors ${
              year === y
                ? "border-accent bg-accent-muted font-medium text-accent"
                : "border-border text-text-secondary hover:text-text-heading"
            }`}
          >
            {y}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={() => openStatementsPdf(data)}
            className="rounded-token-md border border-border px-3 py-2 text-sm text-text-secondary transition-colors hover:border-accent hover:text-text-heading"
            title="Print-ready statements — use the dialog to save as PDF"
          >
            <i className="fa-solid fa-file-pdf mr-1.5 text-xs" />
            PDF
          </button>
          <a
            href={statementsXlsxUrl(year)}
            download
            className="rounded-token-md border border-border px-3 py-2 text-sm text-text-secondary transition-colors hover:border-accent hover:text-text-heading"
            title="The three statements as an Excel workbook"
          >
            <i className="fa-solid fa-file-excel mr-1.5 text-xs" />
            Excel
          </a>
          <a
            href={financeExportUrl(`${year}-01-01`, `${year}-12-31`)}
            download
            className="rounded-token-md border border-border px-3 py-2 text-sm text-text-secondary transition-colors hover:border-accent hover:text-text-heading"
            title="Raw ledger rows"
          >
            <i className="fa-solid fa-file-csv mr-1.5 text-xs" />
            CSV
          </a>
        </div>
      </div>

      <IncomeStatement data={data} onDrill={setDrill} />
      <CashFlowStatement data={data} onDrill={setDrill} />
      {drill && <StatementDrilldown initial={drill} onClose={() => setDrill(null)} />}

      <div className="grid items-start gap-4 lg:grid-cols-2">
        <BalanceSheet data={data} />
        <RecurringCosts data={data} />
      </div>
    </div>
  );
}

function HeadlineCard({
  label,
  value,
  icon,
  tone,
}: {
  label: string;
  value: string;
  icon: string;
  tone?: "danger";
}) {
  return (
    <div className="rounded-token-lg border border-border bg-bg-card p-4">
      <p className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-text-muted">
        <i className={`fa-solid ${icon} text-[0.6rem]`} />
        {label}
      </p>
      <p
        className="mt-1 font-display text-2xl font-semibold"
        style={{
          color: tone === "danger" ? "var(--color-danger)" : "var(--color-text-heading)",
        }}
      >
        {value}
      </p>
    </div>
  );
}

/* ── Shared monthly-table plumbing ─────────────────────────────────────── */

function MonthlyTable({
  months,
  children,
}: {
  months: string[];
  children: React.ReactNode;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full whitespace-nowrap text-xs">
        <thead>
          <tr className="border-b border-border text-right text-[0.6rem] uppercase tracking-wider text-text-muted">
            <th className="px-3 py-2 text-left font-semibold">&nbsp;</th>
            {months.map((m) => (
              <th key={m} className="px-3 py-2 font-semibold">
                {MONTH_LABEL(m)}
              </th>
            ))}
            <th className="px-3 py-2 font-semibold">Total</th>
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

/** A statement cell; clickable (dotted underline on hover) when it has a
 *  non-zero value and a drill-down handler. */
function DrillCell({
  value,
  strong,
  onDrill,
}: {
  value: number;
  strong?: boolean;
  onDrill?: () => void;
}) {
  if (!onDrill || value === 0) return <Cell value={value} strong={strong} />;
  return (
    <button
      onClick={onDrill}
      title="Show breakdown"
      className="cursor-pointer decoration-dotted underline-offset-2 hover:underline"
    >
      <Cell value={value} strong={strong} />
    </button>
  );
}

function LineRow({
  line,
  indent,
  onDrill,
}: {
  line: MonthlyLine;
  indent?: boolean;
  /** monthIndex, or null for the year Total column. */
  onDrill?: (monthIndex: number | null) => void;
}) {
  return (
    <tr className="border-b border-border-light">
      <td className={`px-3 py-1.5 text-text-secondary ${indent ? "pl-6" : ""}`}>
        {line.name}
      </td>
      {line.monthly.map((v, i) => (
        <td key={i} className="px-3 py-1.5 text-right">
          <DrillCell value={v} onDrill={onDrill && (() => onDrill(i))} />
        </td>
      ))}
      <td className="px-3 py-1.5 text-right">
        <DrillCell value={line.total} strong onDrill={onDrill && (() => onDrill(null))} />
      </td>
    </tr>
  );
}

function TotalRow({
  label,
  monthly,
  emphasize,
  stock,
  onDrill,
}: {
  label: string;
  monthly: number[];
  emphasize?: boolean;
  /**
   * Point-in-time balances (e.g. closing cash) don't sum across months —
   * the Total column shows the latest value instead of the sum.
   */
  stock?: boolean;
  /** monthIndex, or null for the year Total column. */
  onDrill?: (monthIndex: number | null) => void;
}) {
  const total = stock
    ? (monthly[monthly.length - 1] ?? 0)
    : Math.round(monthly.reduce((s, v) => s + v, 0) * 100) / 100;
  return (
    <tr className={`border-b border-border ${emphasize ? "bg-bg-secondary/60" : ""}`}>
      <td className="px-3 py-2 font-semibold text-text-heading">{label}</td>
      {monthly.map((v, i) => (
        <td key={i} className="px-3 py-2 text-right">
          <DrillCell value={v} strong onDrill={onDrill && (() => onDrill(i))} />
        </td>
      ))}
      <td className="px-3 py-2 text-right">
        <DrillCell value={total} strong onDrill={onDrill && (() => onDrill(null))} />
      </td>
    </tr>
  );
}

function StatementCard({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-6 overflow-hidden rounded-token-lg border border-border bg-bg-card">
      <div className="border-b border-border px-4 py-3">
        <span className="text-sm font-semibold text-text-heading">{title}</span>
        <span className="ml-2 text-xs text-text-muted">{subtitle}</span>
      </div>
      {children}
    </div>
  );
}

/* ── The three statements ──────────────────────────────────────────────── */

function IncomeStatement({
  data,
  onDrill,
}: {
  data: Statements;
  onDrill: (d: DrillState) => void;
}) {
  const { pnl } = data;
  const valueAt = (l: MonthlyLine, mi: number | null) =>
    mi == null ? l.total : l.monthly[mi];
  const monthAt = (mi: number | null) => (mi == null ? null : pnl.months[mi]);

  const categoryDrill =
    (mode: "amount" | "signed", sign = 1) =>
    (l: MonthlyLine) =>
    (mi: number | null) =>
      onDrill({
        kind: "category",
        accountId: l.accountId,
        label: l.name,
        month: monthAt(mi),
        year: data.year,
        value: valueAt(l, mi) * sign,
        mode,
      });
  const aggregateDrill =
    (label: string, lines: (mi: number | null) => DrillLine[]) =>
    (mi: number | null) =>
      onDrill({
        kind: "aggregate",
        label,
        month: monthAt(mi),
        year: data.year,
        value: 0,
        lines: lines(mi),
      });

  const incomeLines = (mi: number | null): DrillLine[] =>
    pnl.income.map((l) => ({
      accountId: l.accountId,
      name: l.name,
      value: valueAt(l, mi),
      mode: "amount" as const,
    }));
  const expenseLines = (mi: number | null, sign = 1): DrillLine[] =>
    pnl.expense.map((l) => ({
      accountId: l.accountId,
      name: l.name,
      value: valueAt(l, mi) * sign,
      mode: "amount" as const,
    }));

  return (
    <StatementCard title="Income statement" subtitle={`${data.year} · cash basis`}>
      <MonthlyTable months={pnl.months}>
        <TotalRow
          label="Income"
          monthly={pnl.incomeMonthly}
          onDrill={aggregateDrill("Income", incomeLines)}
        />
        {pnl.income.map((l) => (
          <LineRow key={l.accountId} line={l} indent onDrill={categoryDrill("amount")(l)} />
        ))}
        <TotalRow
          label="Expenses"
          monthly={pnl.expenseMonthly}
          onDrill={aggregateDrill("Expenses", (mi) => expenseLines(mi))}
        />
        {pnl.expense.map((l) => (
          <LineRow key={l.accountId} line={l} indent onDrill={categoryDrill("amount")(l)} />
        ))}
        <TotalRow
          label="Net profit / (loss)"
          monthly={pnl.netMonthly}
          emphasize
          onDrill={aggregateDrill("Net profit / (loss)", (mi) => [
            ...incomeLines(mi),
            // Expenses subtract from net profit — shown negated so the
            // breakdown's rows sum exactly to the clicked figure.
            ...expenseLines(mi, -1),
          ])}
        />
      </MonthlyTable>
    </StatementCard>
  );
}

function CashFlowStatement({
  data,
  onDrill,
}: {
  data: Statements;
  onDrill: (d: DrillState) => void;
}) {
  const { cashflow: cf } = data;
  const valueAt = (l: MonthlyLine, mi: number | null) =>
    mi == null ? l.total : l.monthly[mi];
  const monthAt = (mi: number | null) => (mi == null ? null : cf.months[mi]);

  const categoryDrill = (l: MonthlyLine) => (mi: number | null) =>
    onDrill({
      kind: "category",
      accountId: l.accountId,
      label: l.name,
      month: monthAt(mi),
      year: data.year,
      value: valueAt(l, mi),
      mode: "signed",
    });
  const toDrillLines = (lines: MonthlyLine[], mi: number | null): DrillLine[] =>
    lines.map((l) => ({
      accountId: l.accountId,
      name: l.name,
      value: valueAt(l, mi),
      mode: "signed" as const,
    }));
  const aggregateDrill =
    (label: string, lines: MonthlyLine[]) => (mi: number | null) =>
      onDrill({
        kind: "aggregate",
        label,
        month: monthAt(mi),
        year: data.year,
        value: 0,
        lines: toDrillLines(lines, mi),
      });

  return (
    <StatementCard title="Cash flow" subtitle={`${data.year} · direct method`}>
      <MonthlyTable months={cf.months}>
        <TotalRow
          label="Operating"
          monthly={cf.operatingMonthly}
          onDrill={aggregateDrill("Operating", cf.operating)}
        />
        {cf.operating.map((l) => (
          <LineRow key={l.accountId} line={l} indent onDrill={categoryDrill(l)} />
        ))}
        {cf.investing.length > 0 && (
          <>
            <TotalRow
              label="Investing"
              monthly={cf.investingMonthly}
              onDrill={aggregateDrill("Investing", cf.investing)}
            />
            {cf.investing.map((l) => (
              <LineRow key={l.accountId} line={l} indent onDrill={categoryDrill(l)} />
            ))}
          </>
        )}
        {cf.financing.length > 0 && (
          <>
            <TotalRow
              label="Financing"
              monthly={cf.financingMonthly}
              onDrill={aggregateDrill("Financing", cf.financing)}
            />
            {cf.financing.map((l) => (
              <LineRow key={l.accountId} line={l} indent onDrill={categoryDrill(l)} />
            ))}
          </>
        )}
        <TotalRow
          label="Net cash flow"
          monthly={cf.netMonthly}
          emphasize
          onDrill={aggregateDrill("Net cash flow", [
            ...cf.operating,
            ...cf.investing,
            ...cf.financing,
          ])}
        />
        <TotalRow label="Closing cash" monthly={cf.closingMonthly} emphasize stock />
      </MonthlyTable>
      <p className="border-t border-border-light px-4 py-2 text-[0.65rem] text-text-muted">
        Opening cash {fmtMoney(cf.opening)} at 1 Jan {data.year}. Set an expense
        account's cash-flow section under Accounts (e.g. Equipment → Investing).
      </p>
    </StatementCard>
  );
}

function BalanceSheet({ data }: { data: Statements }) {
  const b = data.balanceSheet;
  const row = (label: string, v: number, strong = false) => (
    <div
      className={`flex items-center justify-between px-4 py-2 text-sm ${
        strong ? "font-semibold text-text-heading" : "text-text-secondary"
      }`}
    >
      <span>{label}</span>
      <Cell value={v} strong={strong} />
    </div>
  );
  return (
    <div className="overflow-hidden rounded-token-lg border border-border bg-bg-card">
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <div>
          <span className="text-sm font-semibold text-text-heading">Balance sheet</span>
          <span className="ml-2 text-xs text-text-muted">cash basis · today</span>
        </div>
        {b.balances ? (
          <span className="text-xs font-medium" style={{ color: "var(--color-success)" }}>
            <i className="fa-solid fa-circle-check mr-1" />
            Balances
          </span>
        ) : (
          <span className="text-xs font-medium text-warning">
            <i className="fa-solid fa-triangle-exclamation mr-1" />
            Out of balance
          </span>
        )}
      </div>
      <div className="divide-y divide-border-light">
        <div className="px-4 pb-1 pt-3 text-[0.6rem] font-semibold uppercase tracking-wider text-text-muted">
          Assets
        </div>
        {row("Cash at bank", b.cash)}
        {b.liabilities !== 0 && (
          <>
            <div className="px-4 pb-1 pt-3 text-[0.6rem] font-semibold uppercase tracking-wider text-text-muted">
              Liabilities
            </div>
            {row("Liabilities", b.liabilities)}
          </>
        )}
        <div className="px-4 pb-1 pt-3 text-[0.6rem] font-semibold uppercase tracking-wider text-text-muted">
          Equity
        </div>
        {row("Contributed capital", b.contributed)}
        {row("Retained earnings", b.retained)}
        {row("Total equity", b.equityTotal, true)}
      </div>
      <p className="border-t border-border-light px-4 py-2 text-[0.65rem] text-text-muted">
        Management accounts only — receivables, payables, and depreciation live with the
        accountant at year-end.
      </p>
    </div>
  );
}

function RecurringCosts({ data }: { data: Statements }) {
  return (
    <div className="overflow-hidden rounded-token-lg border border-border bg-bg-card">
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <span className="text-sm font-semibold text-text-heading">Recurring costs</span>
        <span className="text-xs text-text-muted">
          seen in ≥2 of the last 4 months
        </span>
      </div>
      {data.recurring.length === 0 ? (
        <p className="px-4 py-6 text-center text-xs text-text-muted">
          No recurring vendors detected yet.
        </p>
      ) : (
        <ul className="divide-y divide-border-light">
          {data.recurring.map((r) => (
            <li key={r.vendor} className="flex items-center gap-3 px-4 py-2.5 text-sm">
              <span className="min-w-0 flex-1 truncate text-text-secondary">{r.vendor}</span>
              <span className="text-[0.65rem] text-text-muted">
                {r.months} mo · last {r.lastDate.slice(5)}
              </span>
              <span className="w-20 text-right font-medium text-text-heading">
                {fmtMoney(r.monthlyAvg)}
              </span>
            </li>
          ))}
          <li className="flex items-center justify-between px-4 py-2.5 text-sm font-semibold text-text-heading">
            <span>Total / month</span>
            <span>{fmtMoney(data.recurringTotal)}</span>
          </li>
        </ul>
      )}
    </div>
  );
}
